/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  $createCodeNode,
  $isCodeNode,
  CODE_LANGUAGE_FRIENDLY_NAME_MAP,
  CODE_LANGUAGE_MAP,
  getLanguageFriendlyName,
} from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import { INSERT_EMBED_COMMAND } from '@lexical/react/LexicalAutoEmbedPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  HeadingTagType,
} from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $isParentElementRTL,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import { $isTableNode, $isTableSelection } from '@lexical/table';
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
  $getNearestNodeOfType,
  $isEditorIsNestedEditor,
  mergeRegister,
} from '@lexical/utils';
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  ElementFormatType,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_MODIFIER_COMMAND,
  LexicalEditor,
  NodeKey,
  OUTDENT_CONTENT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { Dispatch, useCallback, useEffect, useState } from 'react';
import * as React from 'react';

import useModal from '../../hooks/useModal';
import catTypingGif from '../../images/cat-typing.gif';
import { $createStickyNode } from '../../nodes/StickyNode';
import DropDown, { DropDownItem } from '../../ui/DropDown';
import DropdownColorPicker from '../../ui/DropdownColorPicker';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';
import { EmbedConfigs } from '../AutoEmbedPlugin';
import { INSERT_COLLAPSIBLE_COMMAND } from '../CollapsiblePlugin';
import { InsertEquationDialog } from '../EquationsPlugin';
// import {INSERT_EXCALIDRAW_COMMAND} from '../ExcalidrawPlugin';
import {
  INSERT_IMAGE_COMMAND,
  InsertImageDialog,
  InsertImagePayload,
} from '../ImagesPlugin';
import { InsertInlineImageDialog } from '../InlineImagePlugin';
import InsertLayoutDialog from '../LayoutPlugin/InsertLayoutDialog';
import { INSERT_PAGE_BREAK } from '../PageBreakPlugin';
import { InsertPollDialog } from '../PollPlugin';
import { InsertChartDialog } from '../ChartPlugin';
import { InsertTableDialog } from '../TablePlugin';
import FontSize from './fontSize';
import ToolbarOverflowLayout from './ToolbarOverflowLayout';
import { IS_APPLE } from '../../shared/environment';
import LexicalSignatureModal from '../../LexicalDocumentSignature/InsertSignatureUsersModal/signatureModal';
import { useSignatureModal } from '../../LexicalDocumentSignature/SignatureModalHook';
import signatureIcon from '../../../../assets/CustomButton-Icons/signature-Icon.png';

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
};

const rootTypeToRootName = {
  root: 'Root',
  table: 'Table',
};

function getCodeLanguageOptions() {
  const options = [];

  for (const [lang, friendlyName] of Object.entries(
    CODE_LANGUAGE_FRIENDLY_NAME_MAP,
  )) {
    options.push([lang, friendlyName]);
  }

  return options;
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions();

const FONT_FAMILY_OPTIONS = [
  ['Arial', 'Arial'],
  ['Courier New', 'Courier New'],
  ['Georgia', 'Georgia'],
  ['Times New Roman', 'Times New Roman'],
  ['Trebuchet MS', 'Trebuchet MS'],
  ['Verdana', 'Verdana'],
];

const FONT_SIZE_OPTIONS = [
  ['10px', '10px'],
  ['11px', '11px'],
  ['12px', '12px'],
  ['13px', '13px'],
  ['14px', '14px'],
  ['15px', '15px'],
  ['16px', '16px'],
  ['17px', '17px'],
  ['18px', '18px'],
  ['19px', '19px'],
  ['20px', '20px'],
];

const ELEMENT_FORMAT_OPTIONS = {
  center: {
    icon: 'center-align',
    iconRTL: 'center-align',
    name: 'Center Align',
  },
  end: {
    icon: 'right-align',
    iconRTL: 'left-align',
    name: 'End Align',
  },
  justify: {
    icon: 'justify-align',
    iconRTL: 'justify-align',
    name: 'Justify Align',
  },
  left: {
    icon: 'left-align',
    iconRTL: 'left-align',
    name: 'Left Align',
  },
  right: {
    icon: 'right-align',
    iconRTL: 'right-align',
    name: 'Right Align',
  },
  start: {
    icon: 'left-align',
    iconRTL: 'right-align',
    name: 'Start Align',
  },
};

const lineSpacingOptions = [
  { label: "1.0", value: "1.0" },
  { label: "2.0", value: "2.0" },
  { label: "2.5", value: "2.5" },
  { label: "3.0", value: "3.0" },
  { label: "3.5", value: "3.5" },
  { label: "4.0", value: "4.0" },
  { label: "4.5", value: "4.5" },
  { label: "5.0", value: "5.0" },
];

function dropDownActiveClass(active) {
  if (active) {
    return 'active dropdown-item-active';
  } else {
    return '';
  }
}

function BlockFormatDropDown({
  editor,
  blockType,
  rootType,
  disabled = false,
}) {
  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatCheckList = () => {
    if (blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createQuoteNode());
      });
    }
  };

  const formatCode = () => {
    if (blockType !== 'code') {
      editor.update(() => {
        let selection = $getSelection();

        if (selection !== null) {
          if (selection.isCollapsed()) {
            $setBlocksType(selection, () => $createCodeNode());
          } else {
            const textContent = selection.getTextContent();
            const codeNode = $createCodeNode();
            selection.insertNodes([codeNode]);
            selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertRawText(textContent);
            }
          }
        }
      });
    }
  };

  return (
    React.createElement(DropDown, {
      disabled: disabled,
      buttonClassName: "toolbar-item block-controls",
      buttonIconClassName: 'icon block-type ' + blockType,
      buttonLabel: blockTypeToBlockName[blockType],
      buttonAriaLabel: "Formatting options for text style"
    },
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'paragraph'),
        onClick: formatParagraph
      },
        React.createElement("i", { className: "icon paragraph" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Normal")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'h1'),
        onClick: () => formatHeading('h1')
      },
        React.createElement("i", { className: "icon h1" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Heading 1")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'h2'),
        onClick: () => formatHeading('h2')
      },
        React.createElement("i", { className: "icon h2" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Heading 2")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'h3'),
        onClick: () => formatHeading('h3')
      },
        React.createElement("i", { className: "icon h3" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Heading 3")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'bullet'),
        onClick: formatBulletList
      },
        React.createElement("i", { className: "icon bullet-list" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Bullet List")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'number'),
        onClick: formatNumberedList
      },
        React.createElement("i", { className: "icon numbered-list" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Numbered List")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'check'),
        onClick: formatCheckList
      },
        React.createElement("i", { className: "icon check-list" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Check List")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'quote'),
        onClick: formatQuote
      },
        React.createElement("i", { className: "icon quote" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Quote")
      ),
      React.createElement(DropDownItem, {
        className: 'lexcial_editor_dropdown_item ' + dropDownActiveClass(blockType === 'code'),
        onClick: formatCode
      },
        React.createElement("i", { className: "icon code" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Code Block")
      )
    )
  );
}


function Divider(props) {
  return <div className="divider" {...props} />;
}

function FontDropDown({
  editor,
  value,
  style,
  disabled = false,
}) {
  const handleClick = useCallback(
    (option) => {
      editor.update(() => {
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, {
            [style]: option,
          });
        }
      });
    },
    [editor, style],
  );

  const buttonAriaLabel =
    style === 'font-family'
      ? 'Formatting options for font family'
      : 'Formatting options for font size';

  return (
    React.createElement(DropDown, {
      disabled: disabled,
      buttonClassName: 'toolbar-item ' + style,
      buttonLabel: value,
      buttonIconClassName: style === 'font-family' ? 'icon block-type font-family' : '',
      buttonAriaLabel: buttonAriaLabel
    },
      (style === 'font-family' ? FONT_FAMILY_OPTIONS : FONT_SIZE_OPTIONS).map(
        ([option, text]) => (
          React.createElement(DropDownItem, {
            className: `lexcial_editor_dropdown_item ${dropDownActiveClass(value === option)} ${style === 'font-size' ? 'fontsize-item' : ''}`,
            onClick: () => handleClick(option),
            key: option
          },
            React.createElement("span", { className: "drop_down_editor_text" }, text)
          )
        )
      )
    )
  );
}


function ElementFormatDropdown({
  editor,
  value,
  isRTL,
  disabled = false,
}) {
  const formatOption = ELEMENT_FORMAT_OPTIONS[value || 'left'];

  return (
    React.createElement(DropDown, {
      disabled: disabled,
      buttonLabel: formatOption.name,
      buttonIconClassName: `icon ${isRTL ? formatOption.iconRTL : formatOption.icon}`,
      buttonClassName: "toolbar-item spaced alignment",
      buttonAriaLabel: "Formatting options for text alignment"
    },
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: "icon left-align" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Left Align")
      ),
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: "icon center-align" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Center Align")
      ),
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: "icon right-align" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Right Align")
      ),
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: "icon justify-align" }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Justify Align")
      ),
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'start');
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: `icon ${isRTL ? ELEMENT_FORMAT_OPTIONS.start.iconRTL : ELEMENT_FORMAT_OPTIONS.start.icon}` }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Start Align")
      ),
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'end');
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: `icon ${isRTL ? ELEMENT_FORMAT_OPTIONS.end.iconRTL : ELEMENT_FORMAT_OPTIONS.end.icon}` }),
        React.createElement("span", { className: "drop_down_editor_text" }, "End Align")
      ),
      React.createElement(Divider, null),
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: `icon ${isRTL ? 'indent' : 'outdent'}` }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Outdent")
      ),
      React.createElement(DropDownItem, {
        onClick: () => {
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
        },
        className: "lexcial_editor_dropdown_item"
      },
        React.createElement("i", { className: `icon ${isRTL ? 'outdent' : 'indent'}` }),
        React.createElement("span", { className: "drop_down_editor_text" }, "Indent")
      )
    )
  );
}
function LineSpacingDropDown({
  editor,
  value,
  style,
  disabled = false,
}) {
  const handleClick = useCallback(
    (option) => {
      editor.update(() => {
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, {
            [style]: option,
          });
        }
      });
    },
    [editor, style],
  );

  const buttonAriaLabel = 'Formatting options for line spacing';

  return (
    React.createElement(DropDown, {
      disabled: disabled,
      buttonClassName: 'toolbar-item line-spacing',
      // buttonLabel: value,
      buttonIconClassName: 'icon line-spacing-icon',
      buttonAriaLabel: buttonAriaLabel
    },
      lineSpacingOptions.map(({ value: option, label }) => (
        React.createElement(DropDownItem, {
          className: `lexcial_editor_dropdown_item ${dropDownActiveClass(value === option)} ${style === 'line-height' ? 'line-spacing-item' : ''}`,
          onClick: () => handleClick(option),
          key: option
        },
          React.createElement("span", { className: "drop_down_editor_text" }, label)
        )
      ))
    )
  );
}

export default function ToolbarPlugin({
  setIsLinkEditMode,
}) {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] =
    useState('paragraph');
  const [rootType, setRootType] =
    useState('root');
  const [selectedElementKey, setSelectedElementKey] = useState(
    null,
  );
  const [fontSize, setFontSize] = useState('15px');
  const [fontColor, setFontColor] = useState('#000');
  const [bgColor, setBgColor] = useState('#fff');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [elementFormat, setElementFormat] = useState('left');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [modal, showModal] = useModal();
  const [isRTL, setIsRTL] = useState(false);
  const [codeLanguage, setCodeLanguage] = useState('');
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [isImageCaption, setIsImageCaption] = useState(false);
  const [lineSpacing, setLineSpacing] = useState('1.0');
  const {
    isSignatureModalVisible,
    showSignatureModal,
    hideSignatureModal,
  } = useSignatureModal();

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      if (activeEditor !== editor && $isEditorIsNestedEditor(activeEditor)) {
        const rootElement = activeEditor.getRootElement();
        setIsImageCaption(
          !!rootElement?.parentElement?.classList.contains(
            'image-caption-container',
          ),
        );
      } else {
        setIsImageCaption(false);
      }

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));
      setIsCode(selection.hasFormat('code'));
      setIsRTL($isParentElementRTL(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        setRootType('table');
      } else {
        setRootType('root');
      }

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);

        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(
            anchorNode,
            ListNode
          );
          const type = parentList
            ? parentList.getListType()
            : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();

          if (type in blockTypeToBlockName) {
            setBlockType(type);
          }

          if ($isCodeNode(element)) {
            const language = element.getLanguage();
            setCodeLanguage(
              language ? (CODE_LANGUAGE_MAP[language] || language) : ''
            );
            return;
          }
        }
      }

      // Handle buttons
      setFontColor(
        $getSelectionStyleValueForProperty(selection, 'color', '#000'),
      );
      setBgColor(
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#fff',
        ),
      );
      setFontFamily(
        $getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'),
      );
      setLineSpacing(
        $getSelectionStyleValueForProperty(selection, 'line-height', '1.0'),
      );
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline(),
        );
      }

      // If matchingParent is a valid node, pass it's format type
      setElementFormat(
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : parent?.getFormatType() || 'left',
      );
    }
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      setFontSize(
        $getSelectionStyleValueForProperty(selection, 'font-size', '15px'),
      );
    }
  }, [activeEditor, editor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar]);

  // Keep the shared CSS variable --toolbar-height in sync with the actual rendered toolbar height.
  // This ensures the reserved space below the header updates when toolbar items wrap to multiple rows.
  useEffect(() => {
    const toolbarSelector = '.toolbar';
    const updateCssHeight = (el) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const height = Math.round(rect.height);
      document.documentElement.style.setProperty('--toolbar-height', `${height}px`);
    };

    let toolbarEl = document.querySelector(toolbarSelector);
    const shellEl = document.querySelector('.doc-shell');
    const isShellReadOnly = shellEl && shellEl.classList.contains('read-only-mode');
    if (!toolbarEl) {
      // If no toolbar element is present, let existing CSS rules (like read-only override) apply
      document.documentElement.style.removeProperty('--toolbar-height');
    } else if (isShellReadOnly) {
      // If doc-shell is in read-only mode, ensure CSS rule keeps it hidden by removing inline override
      document.documentElement.style.removeProperty('--toolbar-height');
    } else {
      updateCssHeight(toolbarEl);
    }

    // Use ResizeObserver if available to react to wrap changes
    let ro;
    if (typeof ResizeObserver !== 'undefined' && toolbarEl) {
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          updateCssHeight(entry.target);
        }
      });
      ro.observe(toolbarEl);
    }

    // If toolbar isn't present yet, use a MutationObserver to watch for it and attach a ResizeObserver when added
    let mo;
    if (typeof MutationObserver !== 'undefined' && !toolbarEl) {
      mo = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const added of mutation.addedNodes) {
            if (added instanceof HTMLElement && added.matches && added.matches(toolbarSelector)) {
              toolbarEl = added;
              updateCssHeight(toolbarEl);
              if (typeof ResizeObserver !== 'undefined') {
                ro = new ResizeObserver((entries) => {
                  for (const entry of entries) updateCssHeight(entry.target);
                });
                ro.observe(toolbarEl);
              }
              if (mo) mo.disconnect();
              return;
            }
            // Also check children of added nodes
            if (added instanceof HTMLElement) {
              const child = added.querySelector(toolbarSelector);
              if (child) {
                toolbarEl = child;
                updateCssHeight(toolbarEl);
                if (typeof ResizeObserver !== 'undefined') {
                  ro = new ResizeObserver((entries) => {
                    for (const entry of entries) updateCssHeight(entry.target);
                  });
                  ro.observe(toolbarEl);
                }
                if (mo) mo.disconnect();
                return;
              }
            }
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    // Also keep height updated on window resize just in case
    const onResize = () => {
      toolbarEl = document.querySelector(toolbarSelector);
      const shellEl = document.querySelector('.doc-shell');
      const isShellReadOnly = shellEl && shellEl.classList.contains('read-only-mode');
      if (!toolbarEl || isShellReadOnly) {
        document.documentElement.style.removeProperty('--toolbar-height');
        return;
      }
      if (toolbarEl) updateCssHeight(toolbarEl);
    };
    window.addEventListener('resize', onResize);

    return () => {
      if (ro && toolbarEl) ro.unobserve(toolbarEl);
      if (mo) mo.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    activeEditor.getEditorState().read(() => {
      $updateToolbar();
    });
  }, [activeEditor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      activeEditor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      activeEditor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [$updateToolbar, activeEditor, editor]);

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault();
          let url;
          if (!isLink) {
            setIsLinkEditMode(true);
            url = sanitizeUrl('https://');
          } else {
            setIsLinkEditMode(false);
            url = null;
          }
          return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const applyStyleText = useCallback(
    (styles, skipHistoryStack) => {
      activeEditor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: 'historic' } : {},
      );
    },
    [activeEditor],
  );

  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();
        const extractedNodes = selection.extract();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          // We split the first and last node by the selection
          // So that we don't format unselected text inside those nodes
          if ($isTextNode(node)) {
            // Use a separate variable to ensure TS does not lose the refinement
            let textNode = node;
            if (idx === 0 && anchor.offset !== 0) {
              textNode = textNode.splitText(anchor.offset)[1] || textNode;
            }
            if (idx === nodes.length - 1) {
              textNode = textNode.splitText(focus.offset)[0] || textNode;
            }
            /**
             * If the selected text has one format applied
             * selecting a portion of the text, could
             * clear the format to the wrong portion of the text.
             *
             * The cleared text is based on the length of the selected text.
             */
            // We need this in case the selected text only has one format
            const extractedTextNode = extractedNodes[0];
            if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
              textNode = extractedTextNode;
            }

            if (textNode.__style !== '') {
              textNode.setStyle('');
            }
            if (textNode.__format !== 0) {
              textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
            }
            node = textNode;
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [activeEditor]);

  const onFontColorSelect = useCallback(
    (value, skipHistoryStack) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText],
  );

  const onBgColorSelect = useCallback(
    (value, skipHistoryStack) => {
      applyStyleText({ 'background-color': value }, skipHistoryStack);
    },
    [applyStyleText],
  );

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      activeEditor.dispatchCommand(
        TOGGLE_LINK_COMMAND,
        sanitizeUrl('https://'),
      );
    } else {
      setIsLinkEditMode(false);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const onCodeLanguageSelect = useCallback(
    (value) => {
      activeEditor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey);
          if ($isCodeNode(node)) {
            node.setLanguage(value);
          }
        }
      });
    },
    [activeEditor, selectedElementKey],
  );
  const insertGifOnClick = (payload) => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
  };

  const canViewerSeeInsertDropdown = !isImageCaption;
  const canViewerSeeInsertCodeButton = !isImageCaption;

  const generateToolbarItems = useCallback(() => {
    const items = [];
    // Undo
    items.push(
      <button
        key="undo"
        data-key="undo"
        disabled={!canUndo || !isEditable}
        onClick={() => activeEditor.dispatchCommand(UNDO_COMMAND, undefined)}
        title={IS_APPLE ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)'}
        type="button"
        className="toolbar-item spaced"
        aria-label="Undo"
      >
        <i className="format undo" />
      </button>
    );

    // Redo
    items.push(
      <button
        key="redo"
        data-key="redo"
        disabled={!canRedo || !isEditable}
        onClick={() => activeEditor.dispatchCommand(REDO_COMMAND, undefined)}
        title={IS_APPLE ? 'Redo (⌘Y)' : 'Redo (Ctrl+Y)'}
        type="button"
        className="toolbar-item"
        aria-label="Redo"
      >
        <i className="format redo" />
      </button>
    );

    items.push(<Divider key="div-undo-redo" data-key="div-undo-redo" />);

    if (blockType in blockTypeToBlockName && activeEditor === editor) {
      items.push(
        <BlockFormatDropDown
          key="blockFormat"
          data-key="blockFormat"
          disabled={!isEditable}
          blockType={blockType}
          rootType={rootType}
          editor={activeEditor}
        />
      );
      items.push(<Divider key="div-block" data-key="div-block" />);
    }

    if (blockType === 'code') {
      items.push(
        <DropDown
          key="codeLang"
          data-key="codeLang"
          disabled={!isEditable}
          buttonClassName="toolbar-item code-language"
          buttonLabel={getLanguageFriendlyName(codeLanguage)}
          buttonAriaLabel="Select language"
        >
          {CODE_LANGUAGE_OPTIONS.map(([value, name]) => (
            <DropDownItem
              key={value}
              className={`item ${dropDownActiveClass(value === codeLanguage)}`}
              onClick={() => onCodeLanguageSelect(value)}
            >
              <span className="text">{name}</span>
            </DropDownItem>
          ))}
        </DropDown>
      );
    } else {
      items.push(
        <FontDropDown
          key="font"
          data-key="font"
          disabled={!isEditable}
          style="font-family"
          value={fontFamily}
          editor={activeEditor}
        />,
        <Divider key="div-font" data-key="div-font" />,
        <LineSpacingDropDown
          key="lineSpacing"
          data-key="lineSpacing"
          disabled={!isEditable}
          style="line-height"
          value={lineSpacing}
          editor={activeEditor}
        />,
        <Divider key="div-line" data-key="div-line" />,
        <FontSize
          key="fontSize"
          data-key="fontSize"
          selectionFontSize={fontSize.slice(0, -2)}
          editor={activeEditor}
          disabled={!isEditable}
        />,
        <Divider key="div-size" data-key="div-size" />,
        <button
          key="bold"
          data-key="bold"
          disabled={!isEditable}
          onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          className={`toolbar-item spaced ${isBold ? 'active' : ''}`}
          title={IS_APPLE ? 'Bold (⌘B)' : 'Bold (Ctrl+B)'}
          type="button"
          aria-label="Bold"
        >
          <i className="format bold" />
        </button>,
        <button
          key="italic"
          data-key="italic"
          disabled={!isEditable}
          onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          className={`toolbar-item spaced ${isItalic ? 'active' : ''}`}
          title={IS_APPLE ? 'Italic (⌘I)' : 'Italic (Ctrl+I)'}
          type="button"
          aria-label="Italic"
        >
          <i className="format italic" />
        </button>,
        <button
          key="underline"
          data-key="underline"
          disabled={!isEditable}
          onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          className={`toolbar-item spaced ${isUnderline ? 'active' : ''}`}
          title={IS_APPLE ? 'Underline (⌘U)' : 'Underline (Ctrl+U)'}
          type="button"
          aria-label="Underline"
        >
          <i className="format underline" />
        </button>
      );

      if (canViewerSeeInsertCodeButton) {
        items.push(
          <button
            key="code"
            data-key="code"
            disabled={!isEditable}
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
            className={`toolbar-item spaced ${isCode ? 'active' : ''}`}
            title="Insert code block"
            type="button"
            aria-label="Insert code block"
          >
            <i className="format code" />
          </button>
        );
      }

      items.push(
        <button
          key="link"
          data-key="link"
          disabled={!isEditable}
          onClick={insertLink}
          className={`toolbar-item spaced ${isLink ? 'active' : ''}`}
          aria-label="Insert link"
          title="Insert link"
          type="button"
        >
          <i className="format link" />
        </button>,
        <DropdownColorPicker
          key="textColor"
          data-key="textColor"
          disabled={!isEditable}
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting text color"
          buttonIconClassName="icon font-color"
          color={fontColor}
          onChange={onFontColorSelect}
          title="text color"
        />,
        <DropdownColorPicker
          key="bgColor"
          data-key="bgColor"
          disabled={!isEditable}
          buttonClassName="toolbar-item color-picker"
          buttonAriaLabel="Formatting background color"
          buttonIconClassName="icon bg-color"
          color={bgColor}
          onChange={onBgColorSelect}
          title="bg color"
        />,
        <DropDown
          key="moreStyles"
          data-key="moreStyles"
          disabled={!isEditable}
          buttonClassName="toolbar-item spaced"
          buttonLabel=""
          buttonAriaLabel="More styles"
          buttonIconClassName="icon dropdown-more"
        >
          <DropDownItem
            key="strikethrough"
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
            className={`lexcial_editor_dropdown_item ${dropDownActiveClass(isStrikethrough)}`}
            title="Strikethrough"
          >
            <i className="icon strikethrough" />
            <span className="drop_down_editor_text">Strikethrough</span>
          </DropDownItem>
          <DropDownItem
            key="subscript"
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')}
            className={`lexcial_editor_dropdown_item ${dropDownActiveClass(isSubscript)}`}
            title="Subscript"
          >
            <i className="icon subscript" />
            <span className="drop_down_editor_text">Subscript</span>
          </DropDownItem>
          <DropDownItem
            key="superscript"
            onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')}
            className={`lexcial_editor_dropdown_item ${dropDownActiveClass(isSuperscript)}`}
            title="Superscript"
          >
            <i className="icon superscript" />
            <span className="drop_down_editor_text">Superscript</span>
          </DropDownItem>
          <DropDownItem
            key="clear"
            onClick={clearFormatting}
            className="lexcial_editor_dropdown_item"
            title="Clear Formatting"
          >
            <i className="icon clear" />
            <span className="drop_down_editor_text">Clear Formatting</span>
          </DropDownItem>
        </DropDown>
      );

      if (canViewerSeeInsertDropdown) {
        items.push(
          <Divider key="div-insert" data-key="div-insert" />,
          <DropDown
            key="insert"
            data-key="insert"
            disabled={!isEditable}
            buttonClassName="toolbar-item spaced"
            buttonLabel="Insert"
            buttonAriaLabel="Insert node"
            buttonIconClassName="icon plus"
          >
            <DropDownItem
              key="pageBreak"
              onClick={() => activeEditor.dispatchCommand(INSERT_PAGE_BREAK, undefined)}
              className="lexcial_editor_dropdown_item"
            >
              <i className="icon page-break" />
              <span className="drop_down_editor_text">Page Break</span>
            </DropDownItem>
            <DropDownItem
              key="image"
              onClick={() => {
                showModal('Insert Image', (onClose) => (
                  <InsertImageDialog activeEditor={activeEditor} onClose={onClose} />
                ));
              }}
              className="lexcial_editor_dropdown_item"
            >
              <i className="icon image" />
              <span className="drop_down_editor_text">Image</span>
            </DropDownItem>
            <DropDownItem
              key="table"
              onClick={() => {
                showModal('Insert Table', (onClose) => (
                  <InsertTableDialog activeEditor={activeEditor} onClose={onClose} />
                ));
              }}
              className="lexcial_editor_dropdown_item"
            >
              <i className="icon table" />
              <span className="drop_down_editor_text">Table</span>
            </DropDownItem>
            <DropDownItem
              key="chart"
              onClick={() => {
                showModal('Insert Chart', (onClose) => (
                  <InsertChartDialog activeEditor={activeEditor} onClose={onClose} />
                ));
              }}
              className="lexcial_editor_dropdown_item"
            >
              <i className="icon chart" />
              <span className="drop_down_editor_text">Chart</span>
            </DropDownItem>
            <DropDownItem
              key="signature"
              onClick={(e) => {
                e.preventDefault();
                showSignatureModal();
              }}
              className="lexcial_editor_dropdown_item"
            >
              <img
                className="icon signature"
                src={signatureIcon}
                width={20}
                alt="Signature Icon"
                style={{ marginRight: 8 }}
              />
              <span className="drop_down_editor_text">Signature</span>
            </DropDownItem>
          </DropDown>
        );
      }
    }

    items.push(
      <ElementFormatDropdown
        key="elementFormat"
        data-key="elementFormat"
        disabled={!isEditable}
        value={elementFormat}
        editor={activeEditor}
        isRTL={isRTL}
      />
    );

    return items;
  }, [
    activeEditor,
    canUndo,
    canRedo,
    blockType,
    isEditable,
    isBold,
    isItalic,
    isUnderline,
    isCode,
    isLink,
    fontColor,
    bgColor,
    fontFamily,
    fontSize,
    lineSpacing,
    isStrikethrough,
    isSubscript,
    isSuperscript,
    elementFormat,
    codeLanguage,
    editor,
    isRTL,
    canViewerSeeInsertDropdown,
    canViewerSeeInsertCodeButton,
  ]);

  return (
    <div className="toolbar toolbar--responsive">
      <ToolbarOverflowLayout items={generateToolbarItems()} />
      {modal}
      <LexicalSignatureModal
        visible={isSignatureModalVisible}
        onClose={hideSignatureModal}
      />
    </div>
  );
}
